import { useAuthorization } from 'dx-sdk/build/authorization';
import {
  Accordion,
  Button,
  CTAPlannerCard,
  Hero,
  IconCard,
  ItemsGrid,
  LinkArrow,
  NextStepCard,
  Picture,
  PlannerField,
  PlannerFields,
  PlannerQuestion,
  PlannerQuestionnaire,
  TextInput,
  Typography,
} from 'dx-sdk/build/components';
import RadioQuestion from 'dx-sdk/build/components/PlannerQuestion/components/Radio';
import { Arrow, Pencil, PencilAndPaper } from 'dx-sdk/build/icons';
import { Theme as ThemeProvider } from 'dx-sdk/build/providers';
import { formatDealerName } from 'pubweb-smokey/dist/utils/formatters';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { InView } from 'react-intersection-observer';
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom';
import App from '../../../App';
import { smoothScroll } from '../../../components/Shared/SmokeyTransitional/utils/utils';
import { DXAccountContext } from '../../../contexts/DXAccountContext';
import PlannerCTAIcon from '../../../images/portal/cta-planner-card-icon.PNG';
import plannerCTAAccent from '../../../images/portal/eappCTAAccent.svg';
import article from '../../../images/portal/icons/article.svg';
import lightbulb from '../../../images/portal/icons/lightbulb.svg';
import money from '../../../images/portal/icons/money.svg';
import PlannerBudgetHeroBG from '../../../images/portal/planner/budget/PlannerBudgetHeroBG.jpg';
import LandNextStepIcon from '../../../images/portal/planner/land/LandNextStepIcon.svg';
import plannerCardLinkIcon from '../../../images/portal/plannerCardLinkIconYellow.svg';
import ArrowDown from '../../../images/svg/ArrowDown.svg';
import Loading from '../../../images/svg/icons/Loading';
import * as dealerService from '../../../services/dealerService';
import * as homesService from '../../../services/homesService';
import { fireSegmentEvent, parseInitialPropsContext } from '../../../utils';
import { findFeaturedHomes } from '../../../utils/utils';
import BudgetCalculator from '../../components/BudgetCalculator/BudgetCalculator';
import BudgetIcon from '../../components/PlannerIcons/BudgetIcon';
import RecommendedHomes from '../../components/RecommendedHomes/RecommendedHomes';
import {
  confidenceAnswer,
  findBudgetHomes,
  fundingAnswer,
  getEAppPin,
  portalIconProgress,
  roundBudgetNumber,
} from '../../utils/PortalUtils';
import BudgetStyles from './Budget.styled';

const Budget = ({ dealer, homes }) => {
  const dxContext = useContext(DXAccountContext);
  const { isAuthenticated, isLoading } = useAuthorization();
  const [formIsSaved, setFormIsSaved] = useState(false);
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [updatedUserProfile, setUpdatedUserProfile] = useState(null);
  const [saveTimeout, setSaveTimeout] = useState(0);
  const location = useLocation();
  const history = useHistory();
  const [updatedUser, setUpdatedUser] = useState({
    userId: '',
  });

  const [budgetFormSavedHCs, setBudgetFormSavedHCs] = useState([]);
  const [budgetFormSavedProfileIds, setBudgetFormSavedProfileIds] = useState(
    []
  );

  useEffect(() => {
    const items = JSON.parse(localStorage.getItem('budget_fs'));
    if (items) {
      setBudgetFormSavedHCs(items.homeCenters);
      setBudgetFormSavedProfileIds(items.profileIds);
    }
  }, []);

  useEffect(() => {
    if (budgetFormSavedHCs && budgetFormSavedHCs.length) {
      let filteredHCList = [...new Set(budgetFormSavedHCs)];
      let filteredProfileIdList = [...new Set(budgetFormSavedProfileIds)];
      localStorage.setItem(
        'budget_fs',
        JSON.stringify({
          homeCenters: filteredHCList,
          profileIds: filteredProfileIdList,
        })
      );
    }
  }, [budgetFormSavedHCs, budgetFormSavedProfileIds]);

  useEffect(() => {
    let lotsStored =
      budgetFormSavedHCs && budgetFormSavedHCs.length
        ? budgetFormSavedHCs?.map((i) => i)
        : [];
    let profileIdsStored =
      budgetFormSavedProfileIds && budgetFormSavedProfileIds.length
        ? budgetFormSavedProfileIds?.map((i) => i)
        : [];
    if (
      !location?.state?.activeQuestionLink &&
      lotsStored.includes(dealer?.dealerId) &&
      profileIdsStored.includes(updatedUserProfile?.profileId)
    ) {
      setFormIsSaved(true);
    }
  }, [
    budgetFormSavedHCs,
    budgetFormSavedProfileIds,
    updatedUserProfile?.profileId,
  ]);

  useEffect(() => {
    if (location?.state?.activeQuestionLink) {
      history.replace({ state: { dealer, homes } }) +
        setFormIsSaved(false) +
        setActiveQuestion(location?.state?.activeQuestionLink) +
        smoothScroll('#plannerSection', -20);
    }
  }, []);

  const budgetPlannerProgressObj =
    dxContext?.state?.account?.plannerProgress?.find(
      (item) => item.section == 'Budget'
    );

  const portalIcon = portalIconProgress(
    budgetPlannerProgressObj?.percentCompleted || 0
  );

  const eappPin = getEAppPin(dealer.dealerId);

  useEffect(() => {
    if (
      dxContext?.state?.account &&
      dxContext?.state?.account?.profile &&
      !updatedUser.userId
    ) {
      setUpdatedUserProfile(dxContext?.state?.account.profile);
      setUpdatedUser({
        userId: dxContext?.state?.account.userId,
      });
    }
  }, [dxContext?.state?.account]);

  const numbersOnlyRegex = /^-?\d+\.?\d*$/;

  const handleProfileFieldUpdate = (key) => (value) => {
    const newProfileObj = {
      ...updatedUserProfile,
      [key]: value,
    };

    if (key === 'fundingSource') {
      dxContext?.actions?.markContentComplete(
        budgetPlannerProgressObj,
        'budget_question_1'
      );
      setUpdatedUserProfile(newProfileObj);
      dxContext?.actions?.updateProfileRetail(newProfileObj);
    } else if (key === 'budget') {
      if (numbersOnlyRegex.test(value) || value === null || value === '') {
        setUpdatedUserProfile(newProfileObj);
        if (saveTimeout) {
          window.clearTimeout(saveTimeout);
          setSaveTimeout(0);
        }
        setSaveTimeout(
          window.setTimeout(() => {
            dxContext?.actions?.updateProfileRetail(newProfileObj);
            setSaveTimeout(0);
          }, 1000)
        );
      } else {
        return null;
      }

      if (value === null || value === '') {
        dxContext?.actions?.markContentIncomplete(
          budgetPlannerProgressObj,
          'budget_question_2'
        );
      } else {
        dxContext?.actions?.markContentComplete(
          budgetPlannerProgressObj,
          'budget_question_2'
        );
      }
    } else if (key === 'budgetConfidence') {
      dxContext?.actions?.markContentComplete(
        budgetPlannerProgressObj,
        'budget_question_3'
      );
      setUpdatedUserProfile(newProfileObj);
      dxContext?.actions?.updateProfileRetail(newProfileObj);
    } else {
      return null;
    }
  };

  const BackLink = () => {
    return (
      <LinkArrow
        isReversed
        components={{ Root: Link }}
        componentProps={{
          to: { pathname: '/portal/planner', state: { dealer, homes } },
        }}
      >
        Back to MyHome Planner
      </LinkArrow>
    );
  };

  const LandLink = ({ children }) => {
    return (
      <NextStepCard
        {...children}
        components={{
          Link: () => (
            <Link
              className="NextStepCard-link"
              to={{ pathname: '/portal/land', state: { dealer, homes } }}
            >
              <Arrow />
            </Link>
          ),
        }}
      />
    );
  };

  const CustomAction = (value, questionNumber) => {
    return (
      <div
        className="DXButton DXButton-root"
        onClick={() => {
          setFormIsSaved(false) + setActiveQuestion(questionNumber);
        }}
      >
        {value ? (
          <>
            <Pencil /> <span className="hide-on-mobile">Edit</span>
          </>
        ) : (
          <>
            <PencilAndPaper />
            <span className="hide-on-mobile">COMPLETE YOUR ANSWER</span>
          </>
        )}
      </div>
    );
  };

  const generateFundingHeading = (value) => {
    if (value == 'cash' || value == null) {
      return (
        <>
          <h3 className="PlannerQuestion-heading">
            Do you have a budget in mind for your home?
          </h3>
          <p>
            You can always come back and update your budget, but we can begin to
            filter homes based on your estimation.
          </p>
        </>
      );
    } else if (value == 'not_started') {
      return (
        <div className="calculator-heading">
          <h3 className="PlannerQuestion-heading">That’s not a problem!</h3>
          <p>
            Use our calculator to get an estimate of your monthly payment and
            learn more about how debt-to-income ratio, credit score and down
            payment can impact your loan amount.
          </p>
          <p className="calculator-heading-link">
            Jump to:{' '}
            <span onClick={() => smoothScroll('#calculatorSection', -20)}>
              Calculator <img src={ArrowDown} />
            </span>
          </p>
          <h3 className="PlannerQuestion-heading">
            Do you have a budget in mind for your home?
          </h3>
          <p className="calculator-heading-small-p">
            You can always come back and update your budget, but we can begin to
            filter homes based on your estimation.
          </p>
        </div>
      );
    } else if (value == 'pre_approved') {
      return (
        <>
          <h3 className="PlannerQuestion-heading">
            Do you have a budget in mind for your home?
          </h3>
          <p>
            You can always come back and update your budget, but we can begin to
            filter homes based on your estimation.
          </p>
        </>
      );
    } else {
      return null;
    }
  };

  const recommendedHomes = findFeaturedHomes(homes);

  const roundedBudgetNumber = roundBudgetNumber(
    dxContext?.state?.account?.profile?.budget
  );

  const accordionContent = [
    {
      title: 'How can I save for a down payment?',
      content: (
        <>
          <Typography>
            Saving for a down payment is an important step in purchasing your
            future home. Be sure to set a realistic goal, then start saving.
            Here are a few helpful tips:{' '}
          </Typography>
          <ol>
            <li>
              <b>Put a little away each month.</b> <br />
              Consider the minimum amount of income you receive each month
              compared to any debts you have such as utilities, rent, medical
              payments, etc. Commit to putting away a percentage of the leftover
              amount in savings each month.
            </li>
            <li>
              <b>Create a monthly budget calculator.</b> <br />
              Split your debts into types, such as installment and revolving
              debts. Mark down the due dates for each payment. Note seasonal
              spending, such as holiday or back-to-school. By doing this, you
              can plan for expenses and decide in advance how much you are
              willing to spend on each event to stay within your budget.
            </li>
            <li>
              <b>Reduce unnecessary expenses.</b> <br />
              Minimize spontaneous spending to help you stay within your planned
              monthly budget. Things like writing down and sticking to a grocery
              list, cutting out coffee runs, and avoiding new charges to your
              credit card are great ways to stay on track.
            </li>
          </ol>
        </>
      ),
    },
    {
      title: 'How can I improve my credit score?',
      content: (
        <>
          <Typography>
            Payment history, total debt, credit utilization and types of credit
            lines on your report can all contribute to your credit score. When
            applying for a loan, it is a good idea to check your credit report
            so you know what your credit history will look like to a lender.
            Here are some helpful tips that you may consider to establish a
            favorable credit history:
          </Typography>
          <p className="accordion-p">
            If you have little to no credit history:
          </p>
          <ul>
            <li>
              Try a secured credit card, where your credit limit is determined
              by the required security deposit.
            </li>
            <li>
              Take out a credit-builder loan, where the lender deposits the loan
              amount in a savings account. The and the borrower then makes fixed
              monthly payments toward the loan principal and interest to build a
              payment history and boost savings over time. After all payments
              are made, the borrower then gets access to the loan amount.
            </li>
            <li>
              Become an authorized user on the credit card of a family member
              with a high credit score and a reliable repayment history.
            </li>
          </ul>
          <p className="accordion-p">To improve your credit score:</p>
          <ul>
            <li>Pay your bills on time</li>
            <li>Set up automatic payments</li>
            <li>Use credit cards wisely</li>
            <li>Avoid applying for more credit than you need</li>
            <li>Borrow or charge only what you can afford</li>
            <li>Keep your main credit accounts open</li>
            <li>
              Check your credit report for inaccurate or fraudulent information
            </li>
            <li>
              Talk to a credit counselor for help making a plan to manage your
              debts
            </li>
          </ul>
        </>
      ),
    },
    {
      title: 'Who can help me with loan-related questions?',
      content: (
        <Typography>
          Loan specialists can help with all your loan-related questions. They
          are knowledgeable about all the different loan types that are
          available and are licensed by the state to explain the interest rate,
          APR and terms of different loans you might qualify for. When you
          qualify for a home loan, they will contact you to discuss the
          financial terms and requirements, and they will also be available to
          answer any questions you have throughout the process.
        </Typography>
      ),
    },
  ];

  const homesList = useMemo(() => {
    const listArray =
      dxContext?.state?.account?.profile?.budget &&
      dxContext?.state?.account?.profile?.budget > 0
        ? findBudgetHomes(dxContext?.state?.account?.profile?.budget, homes)
        : findFeaturedHomes(homes);

    return listArray?.map((item) => {
      return {
        id: item.serialNumber || item.modelNumber,
        retailHomeObject: {
          ...item,
        },
      };
    });
  }, [recommendedHomes]);

  if (!isAuthenticated && !isLoading) {
    return <Redirect to="/myhome-account-login" />;
  }

  return (
    <ThemeProvider theme={{}}>
      <App dealer={dealer} homes={homes}>
        <BudgetStyles>
          <Helmet>
            <title>Budget | {formatDealerName(dealer?.dealerName)}</title>
            <meta name="robots" content="noindex" />
          </Helmet>

          {dxContext?.state?.globalLoading ? (
            <div className="loading-screen-panel">
              <Loading width="150px" />
            </div>
          ) : (
            <>
              <Hero
                components={{
                  Backdrop: Picture,
                }}
                componentsProps={{
                  Backdrop: {
                    src: PlannerBudgetHeroBG,
                  },
                  Container: {
                    style: {
                      margin: '0px auto',
                    },
                  },
                }}
              >
                <IconCard
                  heading="Budget"
                  icon={
                    <BudgetIcon
                      dashArray={portalIcon?.dashArray}
                      dashOffset={portalIcon?.dashOffset}
                      opacity={portalIcon?.opacity}
                      showStar={portalIcon?.showStar}
                    />
                  }
                  subheading="Back to MyHome Planner"
                  iconOverlay
                  components={{
                    Subheading: BackLink,
                  }}
                >
                  <Typography>
                    Whether you're paying in cash, looking for financing, or
                    just starting to think about your budget, knowing what to
                    ask and ways to save for your purchase can help you feel
                    more prepared.
                  </Typography>
                </IconCard>
              </Hero>
              <section className="recommendations-section">
                <div className="container">
                  <ItemsGrid
                    heading="Recommendations to explore on this page:"
                    items={[
                      {
                        children: (
                          <span
                            onClick={() => smoothScroll('#plannerSection', -20)}
                          >
                            <img src={money} alt="money icon" />
                            Tell us about your budget needs{' '}
                          </span>
                        ),
                      },
                      {
                        children: (
                          <span
                            onClick={() =>
                              smoothScroll('#homeCostsSection', -20)
                            }
                          >
                            <img src={article} alt="article icon" />
                            Read About Home Costs
                          </span>
                        ),
                      },
                      {
                        children: (
                          <span
                            onClick={() =>
                              smoothScroll('#accordionSection', -20)
                            }
                          >
                            <img src={lightbulb} alt="lightbulb icon" />
                            Explore FAQs
                          </span>
                        ),
                      },
                    ]}
                    components={{
                      Item: Button,
                    }}
                    componentsProps={{
                      Heading: {
                        variant: 'p',
                      },
                      Item: {
                        variant: 'outline',
                        color: '#4E5255',
                        size: 'small',
                      },
                    }}
                  />
                </div>
              </section>
              <section className="planner-section" id="plannerSection">
                <div className="container">
                  <div className="planner-box">
                    {updatedUserProfile !== null ? (
                      <>
                        {!formIsSaved ? (
                          <PlannerQuestionnaire
                            activeQuestion={activeQuestion}
                            onSave={() => {
                              setFormIsSaved(true) +
                                setBudgetFormSavedHCs((hc) => [
                                  ...hc,
                                  dealer?.dealerId,
                                ]) +
                                setBudgetFormSavedProfileIds((p) => [
                                  ...p,
                                  updatedUserProfile?.profileId,
                                ]);
                            }}
                            handlers={{
                              onClick: (e) => smoothScroll('.planner-box', -20),
                            }}
                            children={[
                              <PlannerQuestion
                                type="custom"
                                heading="How will you pay for your home?"
                              >
                                <RadioQuestion
                                  values={[
                                    {
                                      label:
                                        'I’m planning on purchasing with cash.',
                                      value: 'cash',
                                    },
                                    {
                                      label: 'I need financing.',
                                      value: 'not_started',
                                    },
                                    {
                                      label: 'I’m pre-approved for financing.',
                                      value: 'pre_approved',
                                    },
                                  ]}
                                  defaultValue={
                                    updatedUserProfile?.fundingSource || null
                                  }
                                  handlers={{
                                    onChange:
                                      handleProfileFieldUpdate('fundingSource'),
                                  }}
                                />
                              </PlannerQuestion>,
                              <PlannerQuestion
                                type="custom"
                                heading={generateFundingHeading(
                                  updatedUserProfile?.fundingSource
                                )}
                              >
                                <TextInput
                                  label="$ Amount"
                                  color="#4E5255"
                                  value={updatedUserProfile?.budget?.toLocaleString(
                                    'en-US'
                                  )}
                                  variant="outline"
                                  fullwidth
                                  id="budget"
                                  handlers={{
                                    onChange: (value) =>
                                      handleProfileFieldUpdate('budget')(
                                        value != ''
                                          ? parseFloat(
                                              value.replaceAll(',', '')
                                            )
                                          : null
                                      ),
                                  }}
                                />
                              </PlannerQuestion>,
                              <PlannerQuestion
                                type="custom"
                                heading="Tell us how confident you are in your budget:"
                              >
                                <RadioQuestion
                                  values={[
                                    {
                                      label:
                                        'I think I get all of the pieces that make up the pie.',
                                      value: 'high',
                                    },
                                    {
                                      label:
                                        'I have a dollar amount I’m aiming for, but I don’t know all the things I need to plan for.',
                                      value: 'medium',
                                    },
                                    {
                                      label:
                                        'I have no idea where to start - I just know what houses I like!',
                                      value: 'low',
                                    },
                                  ]}
                                  defaultValue={
                                    updatedUserProfile?.budgetConfidence || null
                                  }
                                  handlers={{
                                    onChange:
                                      handleProfileFieldUpdate(
                                        'budgetConfidence'
                                      ),
                                  }}
                                />
                              </PlannerQuestion>,
                            ]}
                          />
                        ) : (
                          <PlannerFields
                            heading="Your Budget"
                            subheading="Your Answers"
                          >
                            <PlannerField
                              heading="How you will be purchasing your home"
                              answer={fundingAnswer(
                                updatedUserProfile?.fundingSource
                              )}
                              components={{
                                Action: () =>
                                  CustomAction(
                                    updatedUserProfile?.fundingSource,
                                    1
                                  ),
                              }}
                            />
                            <PlannerField
                              heading="Your budget is"
                              answer={
                                dxContext?.state?.account?.profile?.budget
                                  ? `$${dxContext?.state?.account?.profile?.budget.toLocaleString(
                                      'en-US'
                                    )}`
                                  : 'No budget set'
                              }
                              components={{
                                Action: () =>
                                  CustomAction(
                                    dxContext?.state?.account?.profile?.budget,
                                    2
                                  ),
                              }}
                            />
                            <PlannerField
                              heading="Your budget confidence level is"
                              answer={confidenceAnswer(
                                updatedUserProfile?.budgetConfidence
                              )}
                              components={{
                                Action: () =>
                                  CustomAction(
                                    updatedUserProfile?.budgetConfidence,
                                    3
                                  ),
                              }}
                            />
                          </PlannerFields>
                        )}
                      </>
                    ) : (
                      <div className="planner-loading-screen">
                        <Loading width="100px" />
                      </div>
                    )}
                  </div>
                </div>
              </section>
              {updatedUserProfile?.fundingSource === 'not_started' && (
                <BudgetCalculator />
              )}
              <section className="CTA-section">
                <div className="container">
                  <CTAPlannerCard
                    className="CTAPlannerCard-eapp"
                    accentImg={{ src: plannerCTAAccent, alt: 'CTA Icon' }}
                    clickComponent={{ type: '', link: '' }}
                    chip="Ready to apply for financing?"
                    heading="Start your online application.*"
                    backgroundColor="#ffffff"
                    icon={PlannerCTAIcon}
                    components={{
                      Click: () => (
                        <a
                          target="_blank"
                          href={`/eapp/invitation/invite?code=${eappPin}&appSource=portal`}
                          onClick={() => {
                            fireSegmentEvent('CTA Clicked', {
                              isNav: false,
                              type: 'card',
                              text: 'planner card link icon',
                              eventContext: 'portal_budget_eapp_cta',
                            });
                          }}
                        >
                          <img
                            src={plannerCardLinkIcon}
                            alt="planner card link icon"
                          />
                        </a>
                      ),
                    }}
                  />
                  <div className="eapp-disclaimer">
                    <p>
                      *This online application can be used to submit your
                      application to multiple lenders. Our home centers and
                      salespeople are not mortgage lenders, brokers, or loan
                      originators. The lenders you choose to work with can help
                      you with questions about the lending process or your loan
                      application. If you have trouble with the link or have
                      questions about your home purchase, please let our team
                      know. The application takes about 15 minutes to complete
                      and it cannot be saved to finish later. Co-borrowers must
                      be present to complete the application.
                    </p>
                  </div>
                </div>
              </section>
              <InView
                threshold={0.3}
                triggerOnce
                as="section"
                className="left-heading-section"
                id="homeCostsSection"
                onChange={(inView, entry) =>
                  inView
                    ? dxContext?.actions?.markContentComplete(
                        budgetPlannerProgressObj,
                        'budget_home_costs'
                      )
                    : null
                }
              >
                <div className="container">
                  <div className="left-heading-component">
                    <div className="left-side">
                      <p className="left-heading">Home Costs Simplified</p>
                    </div>
                    <div className="right-side">
                      <Accordion
                        accordions={[
                          {
                            title: 'Mortgage Factors',
                            content: (
                              <Typography>
                                Lender fees, taxes, points and loan rates all
                                contribute to the overall cost of a mortgage.
                                Your actual interest rate can vary based on down
                                payment, loan amount, your credit score,
                                property type and loan term. (The loan term
                                defines the length of the loan –for example, is
                                10,15, 25 or 30 years.) When choosing between
                                multiple lenders, look at the annual percentage
                                rate for the most accurate comparison.
                              </Typography>
                            ),
                            onExpand: () => {
                              fireSegmentEvent('Accordion Expanded', {
                                item: 'Mortgage Factors',
                                type: 'details_expanded_budget_home_costs_simplified',
                                defaultOpen: false,
                                autoClosing: false,
                                eventContext: 'portal_budget_mortgage_factors',
                              });
                            },
                          },
                          {
                            title: 'Construction Factors',
                            content: (
                              <Typography>
                                When buying a manufactured home, you must factor
                                the cost of the land, delivery fees, upgrades
                                and site preparation into your overall
                                construction costs. We recommend employing a
                                local contractor to prepare the land. Potential
                                costs in site preparation can include permits,
                                inspection fees, septic system installation,
                                excavation, utility connection, grading,
                                driveway and flat work. Upgrades such as
                                exterior porches or patios, or a site-built
                                garage can also add to overall costs.
                              </Typography>
                            ),
                            onExpand: () => {
                              fireSegmentEvent('Accordion Expanded', {
                                item: 'Construction Factors',
                                type: 'details_expanded_budget_home_costs_simplified',
                                defaultOpen: false,
                                autoClosing: false,
                                eventContext:
                                  'portal_budget_construction_factors',
                              });
                            },
                          },
                          {
                            title: 'Land Factors',
                            content: (
                              <Typography>
                                Land is an important factor when considering the
                                total cost of purchasing a manufactured home. If
                                you already own land, be sure to factor site
                                preparation costs. If you are purchasing land,
                                find out how much of your loan can be applied to
                                the land purchase. Also factor in, the annual
                                tax amount (and any potential increase in this
                                amount once you add the manufactured home to the
                                land) as well as the cost of connecting
                                utilities.
                              </Typography>
                            ),
                            onExpand: () => {
                              fireSegmentEvent('Accordion Expanded', {
                                item: 'Land Factors',
                                type: 'details_expanded_budget_home_costs_simplified',
                                defaultOpen: false,
                                autoClosing: false,
                                eventContext: 'portal_budget_land_factors',
                              });
                            },
                          },
                          {
                            title: 'Additional Factors',
                            content: (
                              <Typography>
                                In addition to mortgage, construction and land
                                costs, it is important to consider additional
                                costs that you might incur when buying a
                                manufactured home, such as local zoning and deed
                                restriction laws. It is a good idea to factor in
                                maintenance costs as well when budgeting for a
                                new home. Retouching caulking regularly,
                                applying durable roof coating and regular
                                cleaning of siding, HVAC systems, gutter and
                                interiors will all help prevent issues down the
                                road.
                              </Typography>
                            ),
                            onExpand: () => {
                              fireSegmentEvent('Accordion Expanded', {
                                item: 'Additional Factors',
                                type: 'details_expanded_budget_home_costs_simplified',
                                defaultOpen: false,
                                autoClosing: false,
                                eventContext:
                                  'portal_budget_additional_factors',
                              });
                            },
                          },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </InView>
              {homesList && homesList.length > 0 && (
                <RecommendedHomes
                  swiperID="budgetSwiper"
                  heading={
                    dxContext?.state?.account?.profile?.budget &&
                    dxContext?.state?.account?.profile?.budget > 0
                      ? `Homes under ${roundedBudgetNumber}`
                      : 'Recommended Homes'
                  }
                  subHeading={
                    dxContext?.state?.account?.profile?.budget &&
                    dxContext?.state?.account?.profile?.budget > 0
                      ? `Based on your home budget preferences`
                      : `Featured Homes at ${formatDealerName(
                          dealer?.dealerName
                        )}`
                  }
                  listItems={homesList}
                  dealer={dealer}
                  homes={homes}
                  retailEventLabel="portal_budget_recommended_homes"
                />
              )}

              <InView
                triggerOnce
                as="section"
                className="accordion-section"
                id="accordionSection"
                threshold={0.3}
                onChange={(inView, entry) =>
                  inView
                    ? dxContext?.actions?.markContentComplete(
                        budgetPlannerProgressObj,
                        'budget_faq'
                      )
                    : null
                }
              >
                <div className="container">
                  <Accordion
                    heading="FAQs"
                    accordions={accordionContent.map((accordion) => ({
                      ...accordion,
                      onExpand: () => {
                        fireSegmentEvent('Accordion Expanded', {
                          item: accordion.title,
                          type: 'details_expanded_budget_faq',
                          defaultOpen: false,
                          autoClosing: false,
                          eventContext: 'portal_budget_faqs',
                        });
                      },
                    }))}
                  />
                </div>
              </InView>
              <section className="explore-section">
                <div className="container">
                  <ItemsGrid
                    heading="Next Step"
                    items={[
                      {
                        children: {
                          icon: (
                            <img src={LandNextStepIcon} alt="land next step" />
                          ),
                          chip: {
                            text: 'Land',
                            background: '#E2FFC6',
                            color: 'rgba(37, 41, 45, 0.5)',
                          },
                          heading: 'Learn about land',
                        },
                      },
                    ]}
                    components={{
                      Item: LandLink,
                    }}
                  />
                </div>
              </section>
            </>
          )}
        </BudgetStyles>
      </App>
    </ThemeProvider>
  );
};

Budget.getInitialProps = async (ctx) => {
  return await parseInitialPropsContext(ctx, dealerService, homesService).catch(
    (e) => {
      console.error(e);
      return { redirectTo: '/error' };
    }
  );
};

export default Budget;
